import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTimes, FaInfoCircle, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { submitPortalFulfillmentRequest } from '../services/portalFulfillmentRequestService';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
import PortalYourPlanBanner from '../components/PortalYourPlanBanner';
import { usePortalLinkedAccount } from '../hooks/usePortalLinkedAccount';
import { usePortalAccess } from '../hooks/usePortalAccess';
import {
  isStrategyCallItem,
  isAlwaysRequiredItem,
  isHiringServiceItem,
  isOnSiteVisitItem,
  computeTotalPriceFromSelection,
  computeAddOnPricePerMonth,
  buildProgramItemIdsSet,
  computeIsCustomized,
  computeTotalPricePerMonth,
  computeDisplayMonthly,
  clampDeliverableQuantity,
  countUnpricedCaseByCaseItems,
  getDeliverableMaxQuantity,
  getMaxSelectedMinimumTermMonths,
  isAlaCarteItemHiddenFromPurchase,
  isProgramDeliverableCard,
  monthlyAmountForCaseByCaseBid,
} from '../utils/programPricing';
import { usePublishedBidPrices } from '../hooks/usePublishedBidPrices';
import { useCompanyId } from '../hooks/useCompanyId';
import {
  logProgramViewed,
  logProgramCustomized,
  logProgramChosen,
} from '../services/startActivityLog';
import { areAlaCarteItemsExclusiveChoice } from '../utils/alaCarteExclusiveChoice';
import {
  readDeliverableOrderFromSettingsDocs,
  sortCatalogByDeliverableOrder,
} from '../utils/deliverableOrder';
import { isRetiredProgram } from '../utils/retiredPrograms';

const isCqcVisitItem = (item) => /cqc\s*visit/i.test(item?.name || '');
const isAiCallItem = (item) => /automations?|ai\s*receptionist|chatbot/i.test(item?.name || '');
const isWebsiteItem = (item) => /website\s*buildout|fully\s*custom\s*website|buildout|custom\s*website/i.test(item?.name || '');

// Seeded into every selection and can't be deselected: always-included pricing, the 1:1
// Strategy Call, and items flagged "Always Required" on their catalog overview (Portal Access).
const isAutoSelectedItem = (item) =>
  item?.pricingType === 'alwaysIncluded' || isStrategyCallItem(item) || isAlwaysRequiredItem(item);

const formatPrice = (n) =>`$${Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Build-your-own pseudo-program: no features, so the selection starts with only the
// auto-included items (Portal Access, 1:1 Strategy Call) and prices as the à la carte sum.
export const CUSTOM_PROGRAM = {
  name: 'Custom',
  features: {},
  pricePerMonth: 0,
  minimumTerm: 1,
};
export const CUSTOM_TIER = 'Custom';

const ProgramDetail = ({
  selectedProgramProp = null,
  selectedTierProp = '',
  billingCycleProp = null,
  embedded = false,
  checkoutPathProp = null,
  leadIdProp = null,
  proposalMode = false,
  onSaveProposal = null,
  onConfirmProgram = null,
  onProgramEdited = null,
  proposedProgramData = null,
  confirmMode = false,
  initialSelectedDeliverables = null,
  initialDeliverableQuantities = null,
  hideActionButton = false,
  hideProgramDropdown = false,
  // Drops the dropdown + "<Program> Program" title row when the host page has its own title.
  hideHeader = false,
  showGoToProgramsButton = false
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { leadId: leadIdParam } = useParams();
  const leadId = leadIdProp ?? leadIdParam;
  const isStartContext = !embedded && location.pathname.startsWith('/start/');
  const isCrmContext = !embedded && location.pathname.includes('/crm/lead/') && leadId;
  const isPortalContext = !embedded && location.pathname.startsWith('/portal/');
  // A locked portal has no active plan: the dashboard is the program list, and
  // choosing a program checks out directly instead of filing a change request.
  const { isPortalLocked } = usePortalAccess();
  const isLockedPortalContext = isPortalContext && isPortalLocked;
  const backPath = isCrmContext
    ? `/crm/lead/${leadId}/assessment-analysis`
    : isStartContext
      ? '/start/dashboard'
      : isLockedPortalContext
        ? '/portal'
        : '/portal/billing';
  // The back control is icon-only (matching Checkout), so the destination lives
  // in the tooltip / accessible name instead of a visible label.
  const backLabel = isCrmContext
    ? 'Back to Assessment'
    : isStartContext
      ? 'Back to Dashboard'
      : isLockedPortalContext
      ? 'Back to Programs'
      : isPortalContext
        ? 'Back to Billing'
        : 'Back to Programs';
  const checkoutPath = checkoutPathProp ?? (isStartContext ? '/start/checkout' : '/portal/checkout');

  const selectedProgram = selectedProgramProp ?? location.state?.selectedProgram ?? null;
  const selectedTier = selectedTierProp || location.state?.selectedTier || '';
  const initialBillingCycle = billingCycleProp ?? location.state?.billingCycle ?? 'monthly';
  const proposedProgramFromState = location.state?.proposedProgramData ?? null;
  const initialSelectedDeliverablesFromState = location.state?.initialSelectedDeliverables ?? null;
  const initialDeliverableQuantitiesFromState = location.state?.initialDeliverableQuantities ?? null;

  // Published bid prices for this lead/client, keyed by catalog item id — the only thing that
  // gives a case-by-case deliverable a real price.
  // On /start and /portal there's no lead id in the URL: use the signed-in user's own company,
  // the same account checkout charges (the server prices published bids for it).
  const { companyId: ownCompanyId } = useCompanyId();
  const bidPricesByItemId = usePublishedBidPrices(leadId ?? location.state?.leadId ?? (isCrmContext ? null : ownCompanyId));

  const [programs, setPrograms] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [itemOrder, setItemOrder] = useState({});
  // settings/deliverableOrder — CRM-controlled order of the deliverable cards.
  const [deliverableOrderIds, setDeliverableOrderIds] = useState([]);
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedDeliverables, setSelectedDeliverables] = useState(new Set());
  const [deliverableQuantities, setDeliverableQuantities] = useState({});
  const [billingCycle, setBillingCycle] = useState(initialBillingCycle);
  const [billingCycles, setBillingCycles] = useState({});
  const [activeProgram, setActiveProgram] = useState(null);
  const [activeTier, setActiveTier] = useState('');
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [programConfirmedLocally, setProgramConfirmedLocally] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [fetchedProposedProgram, setFetchedProposedProgram] = useState(null);
  // Direct navigation to /start/programdetail carries no router state, so the redirect below has to
  // wait for the lead lookup before deciding there is nothing to show.
  const [leadLookupDone, setLeadLookupDone] = useState(false);
  const programDropdownRef = useRef(null);
  const { account: portalLinkedAccount } = usePortalLinkedAccount();

  const effectiveProposedProgram = proposedProgramData ?? proposedProgramFromState ?? fetchedProposedProgram;

  // Load a saved/suggested program into the builder. Shared by the "See Suggested Program" buttons
  // and by the direct-navigation effect so both produce exactly the same state.
  const adoptProposedProgram = (pp) => {
    if (!pp) return;
    const ids = pp.selectedDeliverables
      ? (Array.isArray(pp.selectedDeliverables) ? pp.selectedDeliverables : Array.from(pp.selectedDeliverables || []))
      : [];
    const idsToAdd = new Set(ids);
    const autoSelectIds = availableItems
      .filter(isAutoSelectedItem)
      .map((item) => item.id);
    autoSelectIds.forEach((id) => idsToAdd.add(id));
    setSelectedDeliverables(idsToAdd);
    setDeliverableQuantities((prev) => ({ ...prev, ...(pp.deliverableQuantities || {}) }));
    setBillingCycle(pp.billingCycle || 'monthly');
    setActiveProgram({
      name: pp.programName?.replace(/\s*Program\s*$/, '') || 'Custom Program',
      features: pp.features || {},
      pricePerMonth: pp.totalPricePerMonth ?? 0,
      minimumTerm: pp.programMinimumTerm ?? 12
    });
    setActiveTier(pp.selectedTier || 'Custom');
    setProgramDropdownOpen(false);
  };
  const isProgramConfirmed = proposedProgramData?.isProgramConfirmed === true || programConfirmedLocally;
  const isLocked = confirmMode && isProgramConfirmed;
  useEffect(() => {
    if (confirmMode) {
      console.log('[ProgramDetail] confirmMode active', { confirmMode, isProgramConfirmed, hasProposedProgramData: !!proposedProgramData, programConfirmedLocally, leadId });
    }
  }, [confirmMode, isProgramConfirmed, proposedProgramData, programConfirmedLocally, leadId]);

  const currentProgram = activeProgram ?? selectedProgram;
  const currentTier = activeTier || selectedTier;
  const baseProgramName = currentProgram?.name || 'Program Details';
  const programFeatures = currentProgram?.features && typeof currentProgram.features === 'object' ? currentProgram.features : {};
  const basePricePerMonth = currentProgram?.pricePerMonth != null ? parseFloat(currentProgram.pricePerMonth) : 0;
  const isLiteProgram = (currentTier || '').toLowerCase().includes('lite') || (baseProgramName || '').toLowerCase().includes('lite');
  const hasTermPricedItem = [...selectedDeliverables].some((id) => {
    const item = availableItems.find((i) => i.id === id);
    return item?.pricingType === 'term';
  });
  const programMinimumTerm = useMemo(() => {
    // A selected service with a catalog minimum term raises the whole program's minimum.
    const maxItemMinimumTerm = getMaxSelectedMinimumTermMonths(selectedDeliverables, availableItems);
    if (billingCycle === 'annual') return Math.max(12, maxItemMinimumTerm);
    if (isLiteProgram && billingCycle !== 'monthly') return Math.max(12, maxItemMinimumTerm);
    return Math.max(hasTermPricedItem ? 6 : 1, maxItemMinimumTerm);
  }, [billingCycle, isLiteProgram, hasTermPricedItem, selectedDeliverables, availableItems]);

  const deliverableItems = useMemo(() => {
    const items = availableItems.filter((item) => isProgramDeliverableCard(item, selectedDeliverables.has(item.id)));
    return sortCatalogByDeliverableOrder(items, deliverableOrderIds);
  }, [availableItems, selectedDeliverables, deliverableOrderIds]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weeks = [
    [1, 2, 3, 4, 5, 6, 7],
    [8, 9, 10, 11, 12, 13, 14],
    [15, 16, 17, 18, 19, 20, 21],
    [22, 23, 24, 25, 26, 27, 28],
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach((docSnap) => {
            const programData = docSnap.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!programData.name) programData.name = docSnap.id;
              programsData[docSnap.id] = programData;
            }
          });
          setPrograms(programsData);
        }
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        setAvailableItems(itemsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        const orderSnapshot = await getDocs(collection(db, 'settings'));
        const orderData = orderSnapshot.docs.find((d) => d.id === 'itemOrder');
        if (orderData) setItemOrder(orderData.data().order || {});
        setDeliverableOrderIds(readDeliverableOrderFromSettingsDocs(orderSnapshot.docs));
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach((d) => { billingData[d.id] = d.data(); });
          setBillingCycles(billingData);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Sync billing cycle from parent when embedded
  useEffect(() => {
    if (embedded && billingCycleProp != null) setBillingCycle(billingCycleProp);
  }, [embedded, billingCycleProp]);

  // Fetch proposed program from lead when in Start context (find lead by current user email)
  useEffect(() => {
    if (!isStartContext || !currentUser?.email || proposedProgramData || embedded) {
      // Nothing to wait for — let the redirect guard proceed.
      setLeadLookupDone(true);
      return undefined;
    }
    let cancelled = false;
    const fetchLead = async () => {
      try {
        let accountsSnapshot = await getMyAccountSnapshot('Leads');
        if (cancelled) return;
        if (!accountsSnapshot.empty) {
          const data = accountsSnapshot.docs[0].data();
          if (data?.proposedProgram) setFetchedProposedProgram(data.proposedProgram);
        }
      } catch (err) {
        if (!cancelled) console.error('Error fetching lead proposed program:', err);
      } finally {
        if (!cancelled) setLeadLookupDone(true);
      }
    };
    fetchLead();
    return () => { cancelled = true; };
  }, [isStartContext, currentUser?.email, proposedProgramData, embedded]);

  // Direct navigation (bookmark, emailed link) to /start/programdetail: there is no router state, so
  // open the lead's suggested program instead of bouncing back to the program list.
  useEffect(() => {
    if (embedded || !isStartContext) return;
    if (selectedProgram || activeProgram) return;
    // Gate on the catalog load rather than a non-empty catalog, so an empty alaCarteItems can never
    // leave the page stuck on the loader below.
    if (isLoading || !fetchedProposedProgram) return;
    adoptProposedProgram(fetchedProposedProgram);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, isStartContext, selectedProgram, activeProgram, isLoading, fetchedProposedProgram, availableItems]);

  // Proposal mode or proposedProgram: initialize from saved custom program data
  useEffect(() => {
    const selectedInit = initialSelectedDeliverables ?? initialSelectedDeliverablesFromState;
    const qtyInit = initialDeliverableQuantities ?? initialDeliverableQuantitiesFromState;
    if ((!proposalMode && !proposedProgramData && !proposedProgramFromState) || !selectedInit || availableItems.length === 0) return;
    const ids = selectedInit instanceof Set ? Array.from(selectedInit) : (Array.isArray(selectedInit) ? selectedInit : []);
    const idsToAdd = new Set(ids);
    const autoSelectIds = availableItems
      .filter(isAutoSelectedItem)
      .map((item) => item.id);
    autoSelectIds.forEach((id) => idsToAdd.add(id));
    setSelectedDeliverables(idsToAdd);
    if (qtyInit && typeof qtyInit === 'object') {
      setDeliverableQuantities((prev) => ({ ...prev, ...qtyInit }));
    }
  }, [
    proposalMode,
    proposedProgramData,
    proposedProgramFromState,
    initialSelectedDeliverables,
    initialSelectedDeliverablesFromState,
    initialDeliverableQuantities,
    initialDeliverableQuantitiesFromState,
    availableItems
  ]);

  // Pre-populate selectedDeliverables and deliverableQuantities from program features when program or data loads
  useEffect(() => {
    if (availableItems.length === 0) return;
    if ((proposalMode || proposedProgramData) && initialSelectedDeliverables) return; // proposal/proposedProgram init effect handles it
    const idsToAdd = new Set();
    const quantitiesToSet = {};
    if (currentProgram) {
      Object.entries(programFeatures).forEach(([itemName, featureData]) => {
      const item = availableItems.find((i) => (i.name || '').trim() === (itemName || '').trim());
      // Skip unpromoted items — a program's features can still name an item that has
      // since been pulled from sale (the programs template hides it, but the entry stays).
      if (item && !isAlaCarteItemHiddenFromPurchase(item)) {
        idsToAdd.add(item.id);
        const qty = typeof featureData === 'object' && featureData?.quantity != null
          ? parseInt(featureData.quantity, 10)
          : parseInt(featureData, 10);
        if (!Number.isNaN(qty) && qty >= 1) {
          quantitiesToSet[item.id] = clampDeliverableQuantity(item, qty);
        }
      }
    });
    }
    const autoSelectIds = availableItems
      .filter(isAutoSelectedItem)
      .map((item) => item.id);
    autoSelectIds.forEach((id) => idsToAdd.add(id));
    setSelectedDeliverables(idsToAdd);
    setDeliverableQuantities((prev) => ({ ...prev, ...quantitiesToSet }));
  }, [availableItems, currentProgram, programFeatures]);

  // Initialize quantities for items with quantity controls when they're selected
  useEffect(() => {
    const period = (p) => (p || '').toLowerCase();
    const itemsWithQuantity = availableItems.filter(
      (i) =>
        selectedDeliverables.has(i.id) &&
        (isStrategyCallItem(i) ||
          isHiringServiceItem(i) ||
          (i.hasDeliverable && ['month', 'one time', 'onetime'].includes(period(i.deliverable?.period))))
    );
    if (itemsWithQuantity.length > 0) {
      setDeliverableQuantities((prev) => {
        const next = { ...prev };
        itemsWithQuantity.forEach((item) => {
          if (next[item.id] == null) {
            const raw = isStrategyCallItem(item)
              ? 1
              : isHiringServiceItem(item)
                ? 1
                : parseInt(item.deliverable?.quantity, 10);
            const base = isStrategyCallItem(item) ? 1 : Number.isNaN(raw) ? 1 : Math.max(1, raw);
            next[item.id] = clampDeliverableQuantity(item, base);
          }
        });
        return next;
      });
    }
  }, [availableItems, selectedDeliverables]);

  const getOrderedItems = (department) => {
    const deptItems = availableItems.filter(
      (item) =>
        item.department === department &&
        !/on[- ]?site\s*visit|onsite\s*visit/i.test(item?.name || '') &&
        // Unpromoted items aren't for sale, so they stay out of the comparison table.
        !isAlaCarteItemHiddenFromPurchase(item)
    );
    const savedOrder = itemOrder[department] || [];
    if (savedOrder.length === 0 && deptItems.length > 0) return deptItems;
    const orderedItems = [];
    savedOrder.forEach((orderedItem) => {
      const item = deptItems.find((deptItem) => deptItem.id === orderedItem.id);
      if (item) orderedItems.push(item);
    });
    deptItems.forEach((item) => {
      if (!orderedItems.find((o) => o.id === item.id)) orderedItems.push(item);
    });
    return orderedItems;
  };

  const toggleDepartment = (department) => {
    setCollapsedDepartments((prev) => ({ ...prev, [department]: !prev[department] }));
  };

  const showItemDetails = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  // Set once the lead actually touches the selection, so the Start activity log records real
  // customization rather than whatever the program seeded the picker with.
  const hasEditedSelectionRef = useRef(false);

  const toggleDeliverable = (itemId) => {
    if (isLocked) return;
    const item = availableItems.find((i) => i.id === itemId);
    if (isAutoSelectedItem(item)) return;
    hasEditedSelectionRef.current = true;
    setSelectedDeliverables((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
        const added = availableItems.find((i) => i.id === itemId);
        if (added) {
          for (const id of [...next]) {
            if (id === itemId) continue;
            const other = availableItems.find((i) => i.id === id);
            if (other && areAlaCarteItemsExclusiveChoice(added, other)) {
              next.delete(id);
            }
          }
        }
        const period = (item?.deliverable?.period || '').toLowerCase();
        if (item?.hasDeliverable && ['month', 'one time', 'onetime'].includes(period)) {
          const defaultQty = parseInt(item.deliverable?.quantity, 10) || 1;
          setDeliverableQuantities((q) => ({ ...q, [itemId]: clampDeliverableQuantity(item, defaultQty) }));
        } else if (isHiringServiceItem(item)) {
          setDeliverableQuantities((q) => ({ ...q, [itemId]: clampDeliverableQuantity(item, 1) }));
        }
      }
      return next;
    });
  };

  const adjustDeliverableQuantity = (itemId, delta) => {
    if (isLocked) return;
    hasEditedSelectionRef.current = true;
    setDeliverableQuantities((prev) => {
      const item = availableItems.find((i) => i.id === itemId);
      const current = prev[itemId] ?? 1;
      const stepped = Math.max(1, current + delta);
      const next = item ? clampDeliverableQuantity(item, stepped) : stepped;
      return { ...prev, [itemId]: next };
    });
  };

  const getDeliverableQuantity = (itemId) => {
    const item = availableItems.find((i) => i.id === itemId);
    const fromState = deliverableQuantities[itemId];
    if (fromState != null) return clampDeliverableQuantity(item, fromState);
    const qty = parseInt(item?.deliverable?.quantity, 10);
    const base = Number.isNaN(qty) ? 1 : Math.max(1, qty);
    return clampDeliverableQuantity(item, base);
  };

  const programItemIds = useMemo(
    () => buildProgramItemIdsSet(programFeatures, availableItems),
    [availableItems, programFeatures]
  );

  const isCustomized = useMemo(
    () =>
      computeIsCustomized(
        selectedDeliverables,
        programItemIds,
        deliverableQuantities,
        availableItems,
        programFeatures,
        bidPricesByItemId
      ),
    [selectedDeliverables, programItemIds, deliverableQuantities, availableItems, programFeatures, bidPricesByItemId]
  );

  const programName = isCustomized ? 'Custom Program' : baseProgramName;
  const confirmedProgramName = proposedProgramData?.programName || (programConfirmedLocally ? (programName.endsWith(' Program') ? programName : `${programName} Program`) : '');

  // When program is edited after confirmation, reset confirmed state (only when not locked)
  useEffect(() => {
    if (isLocked || !confirmMode || !isProgramConfirmed || !proposedProgramData) return;
    const currentName = programName.endsWith(' Program') ? programName : `${programName} Program`;
    const savedName = proposedProgramData.programName || '';
    const savedDeliverables = new Set(proposedProgramData.selectedDeliverables || []);
    const savedQuantities = proposedProgramData.deliverableQuantities || {};
    const savedBilling = proposedProgramData.billingCycle || 'monthly';
    const savedTier = proposedProgramData.selectedTier || '';
    const deliverablesMatch = selectedDeliverables.size === savedDeliverables.size &&
      [...selectedDeliverables].every((id) => savedDeliverables.has(id));
    const quantitiesMatch = Object.keys(deliverableQuantities).every((id) =>
      (deliverableQuantities[id] ?? 0) === (savedQuantities[id] ?? 0)
    ) && Object.keys(savedQuantities).every((id) =>
      (deliverableQuantities[id] ?? 0) === (savedQuantities[id] ?? 0)
    );
    const hasChanged = currentName !== savedName ||
      currentTier !== savedTier ||
      billingCycle !== savedBilling ||
      !deliverablesMatch ||
      !quantitiesMatch;
    if (hasChanged) {
      setProgramConfirmedLocally(false);
      onProgramEdited?.();
    }
  }, [isLocked, confirmMode, isProgramConfirmed, proposedProgramData, programName, currentTier, billingCycle, selectedDeliverables, deliverableQuantities, onProgramEdited]);

  // A program built from Custom bills every Implementation Hour; legacy standard programs keep
  // their first hour included. createCheckoutSession prices with the same rule.
  const billEveryImplementationHour = currentTier === CUSTOM_TIER;

  const totalPriceFromSelection = useMemo(
    () =>
      computeTotalPriceFromSelection(
        selectedDeliverables,
        deliverableQuantities,
        availableItems,
        billingCycle,
        programMinimumTerm,
        { billEveryImplementationHour }
      ),
    [selectedDeliverables, deliverableQuantities, availableItems, billingCycle, programMinimumTerm, billEveryImplementationHour]
  );

  const addOnPricePerMonth = useMemo(
    () =>
      computeAddOnPricePerMonth(
        selectedDeliverables,
        deliverableQuantities,
        availableItems,
        billingCycle,
        programItemIds,
        programMinimumTerm,
        { billEveryImplementationHour }
      ),
    [
      selectedDeliverables,
      deliverableQuantities,
      availableItems,
      billingCycle,
      programItemIds,
      programMinimumTerm,
      billEveryImplementationHour,
    ]
  );

  // Case-by-case lines contribute 0 to the two sums above (no catalog price). A bid published to
  // this account gives them a real number, added back on whichever path is in play: the
  // customized path totals the whole selection, the base path only counts non-program add-ons.
  const caseByCaseBidForSelection = useMemo(
    () =>
      [...selectedDeliverables].reduce((sum, itemId) => {
        const item = availableItems.find((i) => i.id === itemId);
        if (item?.pricingType !== 'caseByCase') return sum;
        return sum + monthlyAmountForCaseByCaseBid(item, bidPricesByItemId, programMinimumTerm);
      }, 0),
    [selectedDeliverables, availableItems, bidPricesByItemId, programMinimumTerm]
  );

  const caseByCaseBidForAddOns = useMemo(
    () =>
      [...selectedDeliverables].reduce((sum, itemId) => {
        if (programItemIds.has(itemId)) return sum;
        const item = availableItems.find((i) => i.id === itemId);
        if (item?.pricingType !== 'caseByCase') return sum;
        return sum + monthlyAmountForCaseByCaseBid(item, bidPricesByItemId, programMinimumTerm);
      }, 0),
    [selectedDeliverables, availableItems, bidPricesByItemId, programMinimumTerm, programItemIds]
  );

  const totalPricePerMonth = useMemo(
    () =>
      computeTotalPricePerMonth({
        isCustomized,
        totalPriceFromSelection: totalPriceFromSelection + caseByCaseBidForSelection,
        basePricePerMonth,
        addOnPricePerMonth: addOnPricePerMonth + caseByCaseBidForAddOns,
      }),
    [
      isCustomized,
      totalPriceFromSelection,
      caseByCaseBidForSelection,
      basePricePerMonth,
      addOnPricePerMonth,
      caseByCaseBidForAddOns,
    ]
  );

  const displayPrice = useMemo(
    () => computeDisplayMonthly(totalPricePerMonth, billingCycle, billingCycles),
    [totalPricePerMonth, billingCycle, billingCycles]
  );

  const hasCaseByCaseSelected = [...selectedDeliverables].some((itemId) => {
    const item = availableItems.find((i) => i.id === itemId);
    return item?.pricingType === 'caseByCase';
  });

  // Selected case-by-case deliverables with no published bid yet — shown as "+ N to be priced".
  const unpricedCaseByCaseCount = useMemo(
    () => countUnpricedCaseByCaseItems(selectedDeliverables, availableItems, bidPricesByItemId),
    [selectedDeliverables, availableItems, bidPricesByItemId]
  );
  // An unpriced item makes the total provisional, not unknown: the price of everything that IS
  // priced still carries through to checkout and the saved proposal, with hasUnpricedCaseByCase /
  // unpricedCaseByCaseCount alongside it so consumers can show the "+ N to be priced" remainder.
  const hasUnpricedCaseByCase = unpricedCaseByCaseCount > 0;

  // ---------------------------------------------------------------------------
  // Start-side behaviour logging → CRM Lead overview timeline
  // Only the lead's own shopping on /start counts: CRM (confirmMode/proposalMode)
  // and portal usage of this same component are somebody else's activity.
  // ---------------------------------------------------------------------------
  const shouldLogStartActivity = isStartContext && !!currentUser && !proposalMode && !confirmMode;

  // Deliverable labels carry their quantity, so a logged selection reads
  // "Implementation Hours ×12" rather than losing the number they picked.
  const labelByItemId = useMemo(() => {
    const map = new Map();
    availableItems.forEach((item) => {
      if (!item?.id || !item?.name) return;
      const fromState = deliverableQuantities[item.id];
      const fallback = parseInt(item?.deliverable?.quantity, 10);
      const qty = fromState != null
        ? fromState
        : (Number.isNaN(fallback) ? 1 : Math.max(1, fallback));
      map.set(item.id, qty > 1 ? `${item.name} ×${qty}` : item.name);
    });
    return map;
  }, [availableItems, deliverableQuantities]);

  const selectedDeliverableNames = useMemo(
    () => [...selectedDeliverables].map((id) => labelByItemId.get(id)).filter(Boolean).sort(),
    [selectedDeliverables, labelByItemId]
  );

  // The pooled Implementation Hours line — its quantity IS the hours they chose,
  // which is the number that matters most on a custom program.
  const implementationHoursItem = availableItems.find(isStrategyCallItem) || null;
  const implementationHours =
    implementationHoursItem && selectedDeliverables.has(implementationHoursItem.id)
      ? getDeliverableQuantity(implementationHoursItem.id)
      : 0;

  // Which program they opened
  const loggedProgramViewRef = useRef(null);
  useEffect(() => {
    if (!shouldLogStartActivity || !currentProgram?.name) return;
    const viewKey = `${baseProgramName}|${currentTier || ''}`;
    if (loggedProgramViewRef.current === viewKey) return;
    loggedProgramViewRef.current = viewKey;
    logProgramViewed(currentUser, baseProgramName, currentTier);
  }, [shouldLogStartActivity, currentUser, currentProgram?.name, baseProgramName, currentTier]);

  // What they changed. Trailing debounce so one editing burst is one entry, not one per click.
  useEffect(() => {
    if (!shouldLogStartActivity || !isCustomized || !hasEditedSelectionRef.current) return;
    const timer = setTimeout(() => {
      const added = [...selectedDeliverables]
        .filter((id) => !programItemIds.has(id))
        .map((id) => labelByItemId.get(id))
        .filter(Boolean);
      const removed = [...programItemIds]
        .filter((id) => !selectedDeliverables.has(id))
        .map((id) => labelByItemId.get(id))
        .filter(Boolean);
      logProgramCustomized(currentUser, {
        baseProgramName,
        deliverables: selectedDeliverableNames,
        added,
        removed,
        implementationHours,
        pricePerMonth: Math.round(totalPricePerMonth || 0),
        billingCycle,
      });
    }, 3000);
    return () => clearTimeout(timer);
  }, [
    shouldLogStartActivity,
    isCustomized,
    currentUser,
    baseProgramName,
    selectedDeliverables,
    selectedDeliverableNames,
    programItemIds,
    labelByItemId,
    implementationHours,
    totalPricePerMonth,
    billingCycle,
  ]);

  const handlePortalProgramRequest = async () => {
    if (!currentUser?.uid) {
      alert('Please sign in to submit a request.');
      return;
    }
    setRequestSubmitting(true);
    try {
      let accountId = '';
      let requestedBy = currentUser.displayName || currentUser.email || '';
      let company = '';
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const ud = userDoc.data();
          accountId = ud?.accountId || '';
          const full = `${ud?.firstName || ''} ${ud?.lastName || ''}`.trim();
          if (full) requestedBy = full;
          const c = ud?.company;
          if (c && c !== 'Unknown Company') company = c;
        }
      } catch (_) {}
      const featuresToSave = isCustomized
        ? Object.fromEntries(
            [...selectedDeliverables]
              .map((id) => {
                const item = availableItems.find((i) => i.id === id);
                const qty = getDeliverableQuantity(id);
                return item ? [item.name, { quantity: String(qty) }] : null;
              })
              .filter(Boolean)
          )
        : programFeatures;
      const deliverableLines = [...selectedDeliverables].map((id) => {
        const item = availableItems.find((i) => i.id === id);
        return {
          id,
          name: item?.name || id,
          department: item?.department,
          quantity: getDeliverableQuantity(id),
          pricingType: item?.pricingType,
        };
      });
      const fullProgramName = programName.endsWith(' Program') ? programName : `${programName} Program`;
      await submitPortalFulfillmentRequest({
        kind: 'program',
        source: 'programdetail',
        details: {
          programName: fullProgramName,
          selectedTier: isCustomized ? 'Custom' : currentTier,
          billingCycle,
          selectedDeliverableIds: [...selectedDeliverables],
          deliverableLines,
          deliverableQuantities: { ...deliverableQuantities },
          totalPricePerMonth,
          displayPriceMonthly: displayPrice,
          programMinimumTerm,
          hasCaseByCaseSelected,
          hasUnpricedCaseByCase,
          unpricedCaseByCaseCount,
          features: featuresToSave,
          isCustomized,
          leadId: leadId ?? location.state?.leadId ?? null,
        },
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        accountId,
        requestedBy,
        company,
      });
      alert('Your request has been submitted. Our team will follow up shortly.');
      navigate('/portal/billing');
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Could not submit request.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return { name: 'Unknown Program', features: {} };
    }
    return {
      name: program.name || 'Unknown Program',
      features: program.features && typeof program.features === 'object' ? program.features : {}
    };
  };

  const getOrderedPrograms = () => {
    if (!programs) return [];
    const tailOrder = ['Lite', 'Plus', 'Pro', 'Premier', 'BBA Bundle'];
    const orderIndex = (key) => {
      const i = tailOrder.indexOf(key);
      return i >= 0 ? i : 999;
    };
    // Retired programs are left out of the dropdown and comparison, but a saved proposal or a
    // current client on one still loads it by key from `programs`.
    return Object.entries(programs)
      .filter(([key, program]) => !isRetiredProgram(key, program))
      .sort(([keyA], [keyB]) => orderIndex(keyA) - orderIndex(keyB));
  };

  const getOrderedProgramKeys = () => getOrderedPrograms().map(([key]) => key);

  // Load a program's own deliverables into the selection. The pre-populate effect skips this work
  // whenever a saved proposal seeded the selection, so picking from the dropdown has to seed
  // explicitly — otherwise the header would switch programs while the deliverables stayed on the
  // previous program's set.
  const seedDeliverablesFromProgram = (features) => {
    if (availableItems.length === 0) return;
    const idsToAdd = new Set();
    const quantitiesToSet = {};
    Object.entries(features || {}).forEach(([itemName, featureData]) => {
      const item = availableItems.find((i) => (i.name || '').trim() === (itemName || '').trim());
      if (item && !isAlaCarteItemHiddenFromPurchase(item)) {
        idsToAdd.add(item.id);
        const qty = typeof featureData === 'object' && featureData?.quantity != null
          ? parseInt(featureData.quantity, 10)
          : parseInt(featureData, 10);
        if (!Number.isNaN(qty) && qty >= 1) {
          quantitiesToSet[item.id] = clampDeliverableQuantity(item, qty);
        }
      }
    });
    availableItems
      .filter(isAutoSelectedItem)
      .forEach((item) => idsToAdd.add(item.id));
    setSelectedDeliverables(idsToAdd);
    setDeliverableQuantities((prev) => ({ ...prev, ...quantitiesToSet }));
  };

  const selectProgramFromDropdown = (programKey) => {
    const program = programs[programKey];
    if (!program) return;
    const validated = validateProgram(program);
    const isLite = (programKey || '').toLowerCase().includes('lite') || (validated.name || '').toLowerCase().includes('lite');
    const liteMinTerm = billingCycle === 'monthly' ? 1 : 12;
    setActiveProgram({
      name: validated.name,
      features: validated.features,
      pricePerMonth: program.pricePerMonth ?? 0,
      minimumTerm: program.minimumTerm ?? (isLite ? liteMinTerm : 12)
    });
    setActiveTier(programKey);
    seedDeliverablesFromProgram(validated.features);
    setProgramDropdownOpen(false);
  };

  // Custom carries no features, so seeding resets the selection to just the auto-included items
  // (Portal Access, 1:1 Strategy Call).
  const selectCustomProgramFromDropdown = () => {
    setActiveProgram({ ...CUSTOM_PROGRAM });
    setActiveTier(CUSTOM_TIER);
    seedDeliverablesFromProgram({});
    setProgramDropdownOpen(false);
  };

  const cycleBillingCycle = () => {
    if (isLocked) return;
    const cycles = Object.keys(billingCycles).length > 0 ? Object.keys(billingCycles) : ['monthly', 'quarterly', 'annual'];
    const idx = cycles.indexOf(billingCycle);
    setBillingCycle(cycles[(idx + 1) % cycles.length] || 'monthly');
  };

  // A pasted /start/programdetail link carries no router state and the lead may have no suggested
  // program. Fall back to the first program in the dropdown's order, or Custom when none is
  // offered, so the page always renders. Never bounce them back to the program list.
  useEffect(() => {
    if (embedded || !isStartContext) return;
    if (selectedProgram || activeProgram) return;
    if (isLoading || !leadLookupDone || fetchedProposedProgram) return;
    const firstKey = getOrderedProgramKeys()[0];
    if (firstKey) selectProgramFromDropdown(firstKey);
    else selectCustomProgramFromDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, isStartContext, selectedProgram, activeProgram, isLoading, leadLookupDone, fetchedProposedProgram, programs]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (programDropdownRef.current && !programDropdownRef.current.contains(e.target)) {
        setProgramDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDepartmentColor = (department) => {
    const colors = {
      General: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      Sales: 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200',
      Operations: 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200',
      Finance: 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200',
      Technology: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-200',
      Marketing: 'bg-pink-100 text-pink-800 dark:bg-pink-700 dark:text-pink-200',
      HR: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200'
    };
    return colors[department] || colors.General;
  };

  const scheduleEventsByDay = useMemo(() => {
    const events = {};
    const strategyCallItem = availableItems.find(isStrategyCallItem);
    const strategyQty =
      strategyCallItem && selectedDeliverables.has(strategyCallItem.id) ? getDeliverableQuantity(strategyCallItem.id) : 0;
    const oneOnOneSlots = [2, 6, 9, 13, 16, 20, 23, 27, 3, 5, 10, 12, 17, 19, 24, 26];
    for (let i = 0; i < Math.min(strategyQty, oneOnOneSlots.length); i++) {
      const d = oneOnOneSlots[i];
      events[d] = events[d] || [];
      events[d].push({ label: '1:1 Call', color: 'bg-amber-400 text-amber-900' });
    }
    const hasCqc = availableItems.some((i) => isCqcVisitItem(i) && selectedDeliverables.has(i.id));
    if (hasCqc) {
      [19, 20].forEach((d) => {
        events[d] = events[d] || [];
        events[d].push({ label: 'CQC Visit', color: 'bg-green-500 text-white' });
      });
    }
    const hasOnSite = availableItems.some((i) => isOnSiteVisitItem(i) && selectedDeliverables.has(i.id));
    if (hasOnSite) {
      [2, 3].forEach((d) => {
        events[d] = events[d] || [];
        events[d].push({ label: 'On-Site', color: 'bg-blue-500 text-white' });
      });
    }
    events[17] = events[17] || [];
    events[17].push({ label: 'Check', color: 'bg-green-500 text-white' });
    const hasWebsite = availableItems.some((i) => isWebsiteItem(i) && selectedDeliverables.has(i.id));
    if (hasWebsite) {
      events[12] = events[12] || [];
      events[12].push({ label: 'Website', color: 'bg-indigo-500 text-white' });
    }
    return events;
  }, [availableItems, selectedDeliverables, deliverableQuantities]);

  if (!embedded && !isLoading && programs && Object.keys(programs).length === 0) {
    return (
      <div className="min-h-screen relative">
        {/* Same ambient BBA background the Start Dashboard shows */}
        <PortalBackground />
        <StartSidebar />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-gray-600 dark:text-gray-400">No programs available. <button onClick={() => navigate(backPath)} className="text-red-600 hover:underline">Go back</button></p>
        </div>
      </div>
    );
  }

  // Show loading state when embedded
  if (embedded && isLoading) {
    return (
      <div className="py-8 text-center text-gray-500 dark:text-gray-400">
        Loading program details...
      </div>
    );
  }

  // In Start context a program is always resolved locally (suggested program, else the first one) by
  // the effects above, which run after this render — so hold a loader rather than rendering an empty
  // shell or redirecting.
  const isResolvingStartProgram =
    !embedded && isStartContext && !selectedProgram && !activeProgram && (!leadLookupDone || Object.keys(programs).length > 0);

  if (!embedded && !selectedProgram && !activeProgram && (isLoading || isResolvingStartProgram)) {
    return (
      <div className="min-h-screen relative">
        {/* Same ambient BBA background the Start Dashboard shows */}
        <PortalBackground />
        <StartSidebar />
        <div className="relative flex justify-center py-16">
          <BBALoader size={96} />
        </div>
      </div>
    );
  }

  // Redirect to program selection if no program data was passed (direct navigation) - skip when
  // embedded and in Start context, where the page stands on its own.
  if (!embedded && !isLoading && !selectedProgram && !activeProgram && programs && Object.keys(programs).length > 0) {
    if (isCrmContext && leadId) {
      return <Navigate to={`/crm/lead/${leadId}/assessment-analysis`} replace />;
    }
    return <Navigate to="/portal/billing" replace />;
  }

  const content = (
    <div className={`${embedded ? 'w-full' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'} relative`}>
        {!hideHeader && (
        <div className={`flex items-center justify-between ${embedded ? 'mb-10' : 'mb-8'} relative`}>
          {!embedded ? (
            <button
              onClick={() => navigate(backPath)}
              title={backLabel}
              aria-label={backLabel}
              className="inline-flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <FaArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <div className="w-20" />
          )}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center gap-2">
            {/* Start offers only the build-your-own program, so there is nothing to pick between. */}
            {!hideProgramDropdown && !isStartContext && (
              <div className="relative" ref={programDropdownRef}>
                <button
                  type="button"
                  onClick={() => setProgramDropdownOpen((o) => !o)}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  aria-label="Select program"
                  aria-expanded={programDropdownOpen}
                >
                  <FaChevronDown className={`w-5 h-5 transition-transform ${programDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {programDropdownOpen && !isLocked && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[140px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600">
                    {getOrderedProgramKeys().map((programKey) => {
                      const prog = programs[programKey];
                      const name = prog?.name || programKey;
                      const isActive = currentTier === programKey;
                      return (
                        <button
                          key={programKey}
                          type="button"
                          onClick={() => !isLocked && selectProgramFromDropdown(programKey)}
                          className={`w-full px-4 py-2 text-left text-sm whitespace-nowrap transition-colors ${
                            isActive ? 'bg-red-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {name}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => !isLocked && selectCustomProgramFromDropdown()}
                      className={`w-full px-4 py-2 text-left text-sm whitespace-nowrap transition-colors ${
                        currentTier === CUSTOM_TIER ? 'bg-red-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Custom
                    </button>
                  </div>
                )}
              </div>
            )}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {isStartContext ? 'My Program' : programName.endsWith(' Program') ? programName : `${programName} Program`}
            </h1>
          </div>
          <div className="w-32" />
        </div>
        )}

        {/* A locked portal has no active plan — the stale program summary would
            contradict the "choose a plan" flow, so the banner stays hidden. */}
        {isPortalContext && !isPortalLocked && !embedded && portalLinkedAccount?.program && (
          <PortalYourPlanBanner account={portalLinkedAccount} />
        )}

        <div className={`grid grid-cols-2 gap-6 mb-8 ${embedded ? 'mt-6' : ''}`}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 min-h-[360px]">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">Example Meeting Schedule</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">This is how your month may look on this program.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-1">
                {weekDays.map((d) => (
                  <div key={d} className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-1">
                    {d}
                  </div>
                ))}
              </div>
              {weeks.map((week, weekIdx) => (
                <div key={weekIdx} className="grid grid-cols-7 gap-1">
                  {week.map((day, i) => {
                    const isThu = i === 4;
                    const dayEvents = scheduleEventsByDay[day] || [];
                    return (
                      <div
                        key={`w${weekIdx}-${day}`}
                        className="rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 min-h-[52px] p-1 flex flex-col"
                      >
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{day}</span>
                        {isThu && (
                          <div className="mt-0.5 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-medium truncate">
                            Class
                          </div>
                        )}
                        {dayEvents.map((ev, ei) => (
                          <div key={ei} className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium truncate ${ev.color}`}>
                            {ev.label}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 min-h-[360px] flex flex-col">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Program Details</h2>
            {/* Full-width rows rather than three squat boxes: the label reads on the left and the
                value on the right, and the rows share the leftover height so the card fills out. */}
            <div className="flex-1 flex flex-col gap-3 mb-6">
              <div className="flex-1 min-h-[68px] flex items-center justify-between gap-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">💰</span>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Investment</h3>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    {formatPrice(displayPrice)}
                    {unpricedCaseByCaseCount > 0 && (
                      <span className="text-amber-600 dark:text-amber-400"> + {unpricedCaseByCaseCount}</span>
                    )}
                  </p>
                  {unpricedCaseByCaseCount > 0 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-tight">
                      {unpricedCaseByCaseCount === 1 ? 'item to be priced' : 'items to be priced'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-[68px] flex items-center justify-between gap-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">📅</span>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Minimum Term</h3>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  {programMinimumTerm} {programMinimumTerm === 1 ? 'month' : 'months'}
                </p>
              </div>
              <div
                role={isLocked ? 'presentation' : 'button'}
                tabIndex={isLocked ? -1 : 0}
                onClick={cycleBillingCycle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    cycleBillingCycle();
                  }
                }}
                className={`flex-1 min-h-[68px] flex items-center justify-between gap-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 bg-gray-50 dark:bg-gray-700/30 transition-colors ${isLocked ? 'cursor-default' : 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-500'}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">🔄</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Billing Cycle</h3>
                    {!isLocked && (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">Click to change</p>
                    )}
                  </div>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight capitalize">{billingCycle}</p>
              </div>
            </div>
            {showGoToProgramsButton && (
              <button
                onClick={() => navigate('/start/program')}
                className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors mt-4"
              >
                Go to Programs
              </button>
            )}
            {!hideActionButton && (onSaveProposal ? (
              <button
                onClick={() => onSaveProposal({
                  selectedDeliverables: Array.from(selectedDeliverables),
                  deliverableQuantities: { ...deliverableQuantities },
                  totalPricePerMonth,
                  programMinimumTerm,
                  programName,
                  billingCycle,
                  hasCaseByCaseSelected,
                  hasUnpricedCaseByCase,
                  unpricedCaseByCaseCount
                })}
                className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Save Proposal
              </button>
            ) : confirmMode && isProgramConfirmed ? null : confirmMode ? (
              <button
                disabled={isConfirming}
                onClick={async () => {
                  console.log('[ProgramDetail] Confirm Program clicked', { onConfirmProgram: !!onConfirmProgram, isConfirming, leadId });
                  if (!onConfirmProgram || isConfirming) return;
                  setIsConfirming(true);
                  try {
                    const featuresToSave = isCustomized
                      ? Object.fromEntries(
                          [...selectedDeliverables]
                            .map((id) => {
                              const item = availableItems.find((i) => i.id === id);
                              const qty = deliverableQuantities[id] ?? 1;
                              return item ? [item.name, { quantity: String(qty) }] : null;
                            })
                            .filter(Boolean)
                        )
                      : programFeatures;
                    const data = {
                      programName: programName.endsWith(' Program') ? programName : `${programName} Program`,
                      selectedTier: isCustomized ? 'Custom' : currentTier,
                      billingCycle,
                      selectedDeliverables: Array.from(selectedDeliverables),
                      deliverableQuantities: { ...deliverableQuantities },
                      totalPricePerMonth,
                      programMinimumTerm,
                      hasCaseByCaseSelected,
                      hasUnpricedCaseByCase,
                      unpricedCaseByCaseCount,
                      features: featuresToSave
                    };
                    console.log('[ProgramDetail] Calling onConfirmProgram with data:', data);
                    await onConfirmProgram(data);
                    console.log('[ProgramDetail] onConfirmProgram completed successfully');
                    setProgramConfirmedLocally(true);
                  } catch (err) {
                    console.error('[ProgramDetail] Confirm program error:', err);
                    alert('Failed to save program. Please try again. ' + (err?.message || ''));
                  } finally {
                    setIsConfirming(false);
                  }
                }}
                className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isConfirming ? 'Saving...' : 'Confirm Program'}
              </button>
            ) : (
              <button
                type="button"
                disabled={requestSubmitting}
                onClick={() => {
                  if (isPortalContext && !isPortalLocked) {
                    void handlePortalProgramRequest();
                  } else {
                    if (shouldLogStartActivity) {
                      logProgramChosen(currentUser, {
                        programName,
                        tier: isCustomized ? 'Custom' : currentTier,
                        billingCycle,
                        pricePerMonth: Math.round(totalPricePerMonth || 0),
                        minimumTerm: programMinimumTerm,
                        implementationHours,
                        deliverables: selectedDeliverableNames,
                        isCustomized,
                      });
                    }
                    navigate(checkoutPath, {
                      state: {
                        selectedProgram: {
                          // Charge what is priced today; a case-by-case line still awaiting its bid
                          // gets invoiced separately once that bid publishes.
                          name: programName,
                          pricePerMonth: totalPricePerMonth,
                          minimumTerm: programMinimumTerm
                        },
                        selectedTier: isCustomized ? 'Custom' : currentTier,
                        billingCycle,
                        leadId: leadId ?? location.state?.leadId,
                        customDeliverables: Array.from(selectedDeliverables),
                        customDeliverableQuantities: deliverableQuantities
                      }
                    });
                  }
                }}
                className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {requestSubmitting
                  ? 'Submitting…'
                  : isPortalContext && !isPortalLocked
                    ? 'Request This Program'
                    : 'Choose Program'}
              </button>
            ))}
            {!proposalMode && (
              <>
                {!confirmMode && isStartContext && effectiveProposedProgram && (
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={() => adoptProposedProgram(effectiveProposedProgram)}
                      className="w-full py-3 px-6 rounded-lg text-base font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
                    >
                      See Suggested Program
                    </button>
                  </div>
                )}
                {confirmMode && effectiveProposedProgram && !isProgramConfirmed && (
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => adoptProposedProgram(effectiveProposedProgram)}
                      className="px-6 py-3 rounded-lg text-base font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
                    >
                      See Suggested Program
                    </button>
                  </div>
                )}
                {confirmMode && isProgramConfirmed && (
                  <div className="mt-4 flex flex-col gap-2">
                    <div
                      className="w-full py-3 px-6 rounded-lg font-semibold text-white bg-green-600 cursor-default text-center"
                    >
                      ✓ {confirmedProgramName} confirmed
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProgramConfirmedLocally(false);
                        setProgramDropdownOpen(false);
                        // Reset deliverables to program defaults
                        const idsToAdd = new Set();
                        const quantitiesToSet = {};
                        if (currentProgram) {
                          Object.entries(programFeatures).forEach(([itemName, featureData]) => {
                            const item = availableItems.find((i) => (i.name || '').trim() === (itemName || '').trim());
                            if (item && !isAlaCarteItemHiddenFromPurchase(item)) {
                              idsToAdd.add(item.id);
                              const qty = typeof featureData === 'object' && featureData?.quantity != null
                                ? parseInt(featureData.quantity, 10)
                                : parseInt(featureData, 10);
                              if (!Number.isNaN(qty) && qty >= 1) {
                                quantitiesToSet[item.id] = qty;
                              }
                            }
                          });
                        }
                        const autoSelectIds = availableItems
                          .filter(isAutoSelectedItem)
                          .map((item) => item.id);
                        autoSelectIds.forEach((id) => idsToAdd.add(id));
                        setSelectedDeliverables(idsToAdd);
                        setDeliverableQuantities(quantitiesToSet);
                        setBillingCycle(initialBillingCycle || 'monthly');
                        onProgramEdited?.();
                      }}
                      className="w-full py-2 px-4 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Deliverables</h2>
          <div className="grid grid-cols-3 gap-3">
            {deliverableItems.map((item) => {
              const isAutoSelected = isAutoSelectedItem(item);
              const isRequired = isAlwaysRequiredItem(item);
              const isStrategyCall = isStrategyCallItem(item);
              const isSelected = selectedDeliverables.has(item.id);
              // Gold, not green: selected but still to be priced out via a bid.
              const isToBePriced = item.pricingType === 'caseByCase' && !bidPricesByItemId[item.id];
              const deliverablePeriod = (item.deliverable?.period || '').toLowerCase();
              const hasMonthDeliverable = item.hasDeliverable && deliverablePeriod === 'month';
              const hasOneTimeDeliverable = item.hasDeliverable && (deliverablePeriod === 'one time' || deliverablePeriod === 'onetime');
              const hasQuantityControls = hasMonthDeliverable || hasOneTimeDeliverable || isStrategyCall || isHiringServiceItem(item);
              const quantity = hasQuantityControls ? getDeliverableQuantity(item.id) : 1;
              const maxQ = getDeliverableMaxQuantity(item);
              const atMax = maxQ != null && quantity >= maxQ;
              return (
                <div
                  key={item.id}
                  role={isLocked ? 'presentation' : 'button'}
                  tabIndex={isLocked ? -1 : 0}
                  onClick={() => toggleDeliverable(item.id)}
                  onKeyDown={(e) => !isLocked && (e.key === 'Enter' || e.key === ' ') && toggleDeliverable(item.id)}
                  className={`relative rounded-xl border-2 p-3 flex flex-col items-center justify-center transition-colors ${
                    isLocked ? 'cursor-default' : (isAutoSelected ? 'cursor-default' : 'cursor-pointer')
                  } ${
                    !isSelected
                      ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 hover:border-gray-300 dark:hover:border-gray-500'
                      : isToBePriced
                        ? 'border-amber-500 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                        : 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/30'
                  }`}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      showItemDetails(item);
                    }}
                    className="absolute top-2 left-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    aria-label="View item details"
                  >
                    <FaInfoCircle className="h-4 w-4 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" />
                  </button>
                  {isSelected && (
                    <FaCheck
                      className={`text-xl absolute top-2 right-2 ${
                        isToBePriced ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}
                    />
                  )}
                  <span className="text-2xl mb-1">{item.icon || '📦'}</span>
                  <h3
                    className={`font-semibold text-xs text-center ${
                      !isSelected
                        ? 'text-gray-900 dark:text-white'
                        : isToBePriced
                          ? 'text-amber-800 dark:text-amber-200'
                          : 'text-green-800 dark:text-green-200'
                    }`}
                  >
                    {item.name}
                  </h3>
                  {isSelected && isToBePriced && (
                    <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">To be priced</p>
                  )}
                  {isRequired && isSelected && (
                    <p className="text-[10px] font-medium text-green-700 dark:text-green-300 mt-1">Required · Qty 1</p>
                  )}
                  {hasQuantityControls && isSelected && !isRequired && (
                    <div
                      className="flex items-center gap-2 mt-2"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <button
                        type="button"
                        disabled={isLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustDeliverableQuantity(item.id, -1);
                        }}
                        className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-default"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="min-w-[1.5rem] text-center font-semibold text-gray-900 dark:text-white text-sm">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        disabled={isLocked || atMax}
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustDeliverableQuantity(item.id, 1);
                        }}
                        className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-default"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  )}
                  {hasQuantityControls && !isSelected && !isHiringServiceItem(item) && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Click to add</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {!isLoading && !proposalMode && !confirmMode && (
          <div id="complete-program-comparison" className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 scroll-mt-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center">Complete Program Comparison</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Items</th>
                    {programs && getOrderedProgramKeys().map((programKey) => (
                      <th key={programKey} className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                        {validateProgram(programs[programKey]).name}
                      </th>
                    ))}
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">Your Program</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(() => {
                    const rows = [];
                    const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
                    const programCount = programs ? Object.keys(programs).length + 1 : 1;
                    departments.forEach((dept) => {
                      const deptItems = getOrderedItems(dept);
                      if (deptItems.length > 0) {
                        const isCollapsed = collapsedDepartments[dept];
                        rows.push(
                          <tr key={`dept-${dept}`} className="bg-gray-50 dark:bg-gray-700">
                            <td colSpan={programCount + 1} className="px-6 py-3">
                              <button
                                onClick={() => toggleDepartment(dept)}
                                className="flex items-center space-x-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                              >
                                {isCollapsed ? <FaChevronRight className="h-4 w-4" /> : <FaChevronDown className="h-4 w-4" />}
                                <span>{dept}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">({deptItems.length} items)</span>
                              </button>
                            </td>
                          </tr>
                        );
                        if (!isCollapsed) {
                          deptItems.forEach((item) => {
                            const isDeliverableItem = item.pricingType !== 'child' && !isAutoSelectedItem(item);
                            const parentItem = item.pricingType === 'child' && item.parentItemId
                              ? availableItems.find((i) => i.id === item.parentItemId)
                              : null;
                            const isChildOfAlwaysIncluded = parentItem?.pricingType === 'alwaysIncluded';
                            const isCustomSelected =
                              item.pricingType === 'alwaysIncluded' ||
                              isChildOfAlwaysIncluded ||
                              selectedDeliverables.has(item.id);
                            rows.push(
                              <tr
                                key={item.id}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isDeliverableItem ? 'cursor-pointer' : ''}`}
                                onClick={isDeliverableItem ? () => toggleDeliverable(item.id) : undefined}
                              >
                                <td className="px-6 py-4">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      showItemDetails(item);
                                    }}
                                    className="flex items-center space-x-3 flex-1 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-md transition-colors w-full"
                                  >
                                    <div className="text-2xl">{item.icon || '📦'}</div>
                                    <div className="flex items-center space-x-2">
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">{item.name}</div>
                                      <FaInfoCircle className="h-4 w-4 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" />
                                    </div>
                                  </button>
                                </td>
                                {programs && getOrderedPrograms().map(([programKey, program]) => {
                                  const validatedProgram = validateProgram(program);
                                  const currentQuantity = validatedProgram.features[item.name]?.quantity || '0';
                                  const isCheckbox = currentQuantity === '0';
                                  const isIncluded = validatedProgram.features[item.name] !== undefined;
                                  return (
                                    <td key={programKey} className="px-6 py-4 text-center">
                                      {isIncluded ? (
                                        <div className="flex items-center justify-center">
                                          {isCheckbox ? <FaCheck className="text-green-500 text-sm" /> : <span className="text-sm font-medium text-gray-900 dark:text-white">{currentQuantity}</span>}
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center"><FaTimes className="text-red-500 text-sm" /></div>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-6 py-4 text-center">
                                  <div className="flex items-center justify-center">
                                    {isCustomSelected ? (
                                      ((item.hasDeliverable && ['month', 'one time', 'onetime'].includes((item.deliverable?.period || '').toLowerCase())) || isStrategyCallItem(item) || isHiringServiceItem(item)) && selectedDeliverables.has(item.id) ? (
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                          {getDeliverableQuantity(item.id)}
                                        </span>
                                      ) : (
                                        <FaCheck className="text-green-500 text-sm" />
                                      )
                                    ) : (
                                      <FaTimes className="text-red-500 text-sm" />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        }
                      }
                    });
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md">
            <div className="bg-red-600 rounded-t-lg p-4 relative">
              <h3 className="text-lg font-semibold text-white text-center">Item Details</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="text-4xl">{selectedItem.icon || '📦'}</div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedItem.name}</h4>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getDepartmentColor(selectedItem.department)}`}>
                    {selectedItem.department}
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h5>
                <p className="text-gray-600 dark:text-gray-400">{selectedItem.description || 'No description available.'}</p>
              </div>
              <div className="flex justify-end">
                <button onClick={closeItemModal} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }
  return (
    <div className="min-h-screen relative">
      {/* Same ambient BBA background the Start Dashboard shows */}
      <PortalBackground />
      <StartSidebar />
      <div className="relative">{content}</div>
    </div>
  );
};

export default ProgramDetail;
